// Cruce del grafo con la ejecución: qué nodo del workflow corrió, cuántas
// veces y cuánto costó. Puro, sin React, para poder probarlo contra corridas
// reales.
//
// Por defecto solo entran los pasos que corrieron. Un workflow de 77 pasos
// donde corrieron 31 se lee como 31; los demás se pueden encender, pero no
// son lo que pasó.

// Separación entre columnas y entre filas del mapa. Las posiciones del canvas
// se comprimen a esta grilla (ver `compressPositions`).
const COL_PITCH = 240;
const ROW_PITCH = 110;

// Dos nodos que el editor dejó a menos de esto se consideran de la misma
// columna. Sin tolerancia, un arrastre de tres píxeles abre una columna.
const SNAP = 40;

/** Agrupa coordenadas cercanas y las vuelve índices consecutivos. */
const compressAxis = (values, pitch) => {
  const unique = [...new Set(values.map((value) => Math.round(value / SNAP) * SNAP))].sort(
    (a, b) => a - b
  );
  const index = new Map(unique.map((value, position) => [value, position * pitch]));
  return (value) => index.get(Math.round(value / SNAP) * SNAP) ?? 0;
};

/**
 * Comprime las posiciones reales del canvas.
 *
 * El tablero de un workflow largo mide diez mil píxeles de ancho porque su
 * autor dejó aire entre las ramas. Al mostrar solo lo que corrió, ese aire
 * separa nodos que en la corrida fueron uno detrás del otro. Se conserva el
 * orden y la estructura —quién está a la izquierda de quién, qué comparte
 * carril— y se tira la distancia.
 */
export const compressPositions = (steps) => {
  const placed = steps.filter((step) => step.position);
  const toX = compressAxis(placed.map((step) => step.position.x), COL_PITCH);
  const toY = compressAxis(placed.map((step) => step.position.y), ROW_PITCH);

  // Los pasos sin coordenadas —los crea la API, no el canvas— van a una
  // columna propia al final en vez de amontonarse en el origen.
  const spareColumn = (new Set(placed.map((step) => Math.round(step.position.x / SNAP) * SNAP)).size) * COL_PITCH;
  let spareRow = 0;

  return new Map(
    steps.map((step) => {
      if (!step.position) {
        const position = { x: spareColumn, y: spareRow };
        spareRow += ROW_PITCH;
        return [step.id, { position, positioned: false }];
      }
      return [
        step.id,
        { position: { x: toX(step.position.x), y: toY(step.position.y) }, positioned: true },
      ];
    })
  );
};

/** Lo que la ejecución dice de cada paso del grafo, indexado por `step_id`. */
export const runsByStep = (timeline) => {
  const entries = Array.isArray(timeline) ? timeline : [];
  const byStep = new Map();

  for (const entry of entries) {
    if (entry.kind !== 'step' || !entry.step_id) continue;
    const current = byStep.get(entry.step_id) || {
      runs: 0,
      totalMs: 0,
      coins: 0,
      failed: 0,
      firstSeq: entry.seq,
      lastStatus: null,
      passes: [],
    };
    current.runs += 1;
    current.totalMs += entry.duration_ms || 0;
    current.coins += entry.cost?.coins || 0;
    current.failed += entry.status === 'failed' ? 1 : 0;
    current.lastStatus = entry.status;
    current.passes.push({
      seq: entry.seq,
      pass: entry.pass_number,
      durationMs: entry.duration_ms,
      status: entry.status,
    });
    byStep.set(entry.step_id, current);
  }

  return byStep;
};

/**
 * Nodos y aristas para el mapa de la corrida.
 *
 * @param {Object} graph respuesta de `/executions/{id}/graph`
 * @param {Array} timeline entradas de `/executions/{id}/timeline`
 * @param {Array} notExecuted `not_executed[]` del mismo timeline
 * @param {boolean} showSkipped incluir los pasos que no corrieron
 */
export const buildRunMap = ({ graph, timeline, notExecuted = [], showSkipped = false }) => {
  const steps = graph?.steps || [];
  const executed = runsByStep(timeline);
  const skipInfo = new Map((notExecuted || []).map((item) => [item.step_id, item]));

  const visible = steps.filter((step) => executed.has(step.id) || showSkipped);
  const layout = compressPositions(visible);
  const visibleIds = new Set(visible.map((step) => step.id));

  const nodes = visible.map((step) => {
    const run = executed.get(step.id);
    const placement = layout.get(step.id);
    const skipped = skipInfo.get(step.id);

    return {
      id: step.id,
      type: 'runStep',
      position: placement.position,
      draggable: false,
      data: {
        name: step.name,
        stepType: step.type,
        stepOrder: step.step_order,
        positioned: placement.positioned,
        ran: Boolean(run),
        runs: run?.runs || 0,
        totalMs: run?.totalMs || 0,
        coins: run?.coins || 0,
        failed: run?.failed || 0,
        firstSeq: run?.firstSeq ?? null,
        lastStatus: run?.lastStatus || null,
        passes: run?.passes || [],
        skippedReason: skipped?.reason || null,
        decidedBy: skipped?.decided_by || null,
      },
    };
  });

  // Las aristas salen del grafo, no de un camino registrado: se sabe qué nodos
  // corrieron, no por cuál arista se pasó de uno al siguiente. En snapshots
  // viejos `edges` viene vacío y `dependencies` es lo único que hay.
  const fromCanvas = (graph?.edges || [])
    .filter((edge) => visibleIds.has(edge.source) && visibleIds.has(edge.target))
    .map((edge) => ({
      id: edge.id || `${edge.source}-${edge.target}`,
      source: edge.source,
      target: edge.target,
      branch: edge.sourceHandle || null,
    }));

  // `dependencies` es la topología que siempre está; `canvas_edges` agrega la
  // rama de cada condición y falta en los snapshots viejos. Se unen las dos:
  // quedarse con una sola deja nodos sueltos que sí estaban conectados.
  const seen = new Set(fromCanvas.map((edge) => `${edge.source}->${edge.target}`));
  const fromDependencies = visible.flatMap((step) =>
    (step.dependencies || [])
      .filter((dependency) => visibleIds.has(dependency))
      .filter((dependency) => !seen.has(`${dependency}->${step.id}`))
      .map((dependency) => ({
        id: `${dependency}-${step.id}`,
        source: dependency,
        target: step.id,
        branch: null,
      }))
  );

  const edges = [...fromCanvas, ...fromDependencies];

  return {
    nodes,
    edges,
    shown: visible.length,
    total: steps.length,
    hidden: steps.length - visible.length,
  };
};
